import React, { useState, useCallback, useMemo } from 'react';
import { DataFilterField, IDataFilterFieldCommonProps } from './DataFilterField';
import { StringMatchType } from '@decisions/server/DecisionsFramework/Design/Report/Service/FilterFactory';
import { StringMatch, newStringMatch } from '@decisions/server/DecisionsFramework/Design/Page/Filters/StringMatch';
import { IStringComboOption } from '../StringComboBox';
import { debounce } from 'debounce';
import { DEFAULT_STRING_MATCH_TYPE_LABELS, getLabelForStringMatchType } from './utils/StringMatchUtils';

export interface IStringMatchFieldProps extends IDataFilterFieldCommonProps<StringMatch> {}

/**
 * A search string field with personalization controls.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function StringMatchField({ onChange, personalization, id, value, validation }: IStringMatchFieldProps) {
  const [stringValue, setStringValue] = useState(value.SearchString ?? '');
  const onValueChange = useCallback(
    (newValue: { MatchType?: StringMatchType; SearchString?: string }) => {
      onChange(
        newStringMatch({
          ...value,
          ...newValue,
        })
      );
    },
    [onChange, value]
  );

  const emitStringChange = useMemo(() => debounce(onValueChange, 500), [onValueChange]);

  const setFieldValue = useCallback(
    (SearchString: string) => {
      setStringValue(SearchString ?? '');
      emitStringChange.clear();
      emitStringChange({ SearchString });
    },
    [emitStringChange]
  );

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    emitStringChange.clear();
    onValueChange({ SearchString: e.target.value });
  };

  return (
    <DataFilterField
      value={value}
      fieldText={stringValue}
      id={id}
      onChange={onValueChange}
      onFieldChange={setFieldValue}
      validation={validation}
      personalization={personalization}
      onBlur={onBlur}
    />
  );
}

// TODO need translation of these?
export function getStringMatchOptions(): IStringComboOption<StringMatchType>[] {
  return Array.from(DEFAULT_STRING_MATCH_TYPE_LABELS.keys()).map(value => ({
    value,
    text: getLabelForStringMatchType(value),
  }));
}
